import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FlaskConical, Users, School, Mail, Phone, Calendar, ChevronDown, ChevronUp,
  Check, X, Clock, Download, Send, Shield, AlertTriangle, CalendarPlus, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Mail },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-300', icon: Check },
  onboarded: { label: 'Onboarded', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: Users },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700 border-red-300', icon: X },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-300', icon: Ban },
  expired: { label: 'Expired', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: Clock },
  data_purged: { label: 'Data Purged', color: 'bg-gray-200 text-gray-500 border-gray-300', icon: Ban },
};

const BetaManagementTab = () => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [extending, setExtending] = useState(null);
  const [extendDate, setExtendDate] = useState('');
  const [extendReason, setExtendReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(null);

  const fetchSignups = async () => {
    try {
      const res = await axios.get(`${API}/beta-signups`, authHeader());
      setSignups(res.data || []);
    } catch { toast.error('Failed to load beta signups'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSignups(); }, []);

  const updateStatus = async (signupId, newStatus) => {
    try {
      const res = await axios.patch(`${API}/beta-signups/${signupId}/status`, { status: newStatus }, authHeader());
      toast.success(res.data.message);
      if (res.data.admin_email) {
        toast.success(`Credentials: ${res.data.admin_email}`, { duration: 10000 });
      }
      fetchSignups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleExtend = async (signupId) => {
    if (!extendDate.trim()) { toast.error('Enter a new end date'); return; }
    try {
      const res = await axios.patch(`${API}/beta-signups/${signupId}/extend`, {
        new_end_date: extendDate,
        reason: extendReason,
      }, authHeader());
      toast.success(res.data.message);
      setExtending(null);
      setExtendDate('');
      setExtendReason('');
      fetchSignups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to extend'); }
  };

  const handleCancel = async (signupId) => {
    try {
      const res = await axios.patch(`${API}/beta-signups/${signupId}/cancel`, {
        reason: cancelReason,
      }, authHeader());
      toast.success(res.data.message);
      setConfirmCancel(null);
      setCancelReason('');
      fetchSignups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const downloadAgreement = async (signupId) => {
    // v6.32.61: agreement-pdf is now Super-Admin-gated; window.open() can't
    // send Authorization header, so fetch+blob and trigger a programmatic
    // download instead.
    try {
      const res = await fetch(`${API}/beta-signups/${signupId}/agreement-pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_Beta_Agreement_${signupId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(`Failed to download agreement: ${e.message}`);
    }
  };

  const downloadSchoolData = async (schoolName) => {
    try {
      // Look up school_id by name
      const token = getToken();
      const res = await axios.get(`${API}/schools/search?name=${encodeURIComponent(schoolName)}`, authHeader());
      const school = res.data;
      if (school?.school_id) {
        window.open(`${API}/export/school/${school.school_id}`, '_blank');
        toast.success('School data export started');
      } else {
        toast.error('School not found in system — may not have been onboarded yet');
      }
    } catch {
      toast.error('School not found — data export requires an active school account');
    }
  };

  const filtered = statusFilter === 'all' ? signups : signups.filter(s => s.status === statusFilter);
  const countByStatus = (s) => signups.filter(i => i.status === s).length;

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="beta-management-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const count = countByStatus(key);
          return (
            <div
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              className={`p-4 border-2 text-left transition-all cursor-pointer ${
                statusFilter === key ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-gray-200 hover:border-gray-300'
              } bg-white`}
              data-testid={`beta-filter-${key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={cfg.color.split(' ')[1]} />
                <span className="text-xs font-semibold uppercase">{cfg.label}</span>
              </div>
              <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{count}</div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {filtered.length} of {signups.length} applications
          {statusFilter !== 'all' && <button onClick={() => setStatusFilter('all')} className="ml-2 text-[#002FA7] underline text-xs">Show all</button>}
        </p>
      </div>

      {/* Signups List */}
      <div className="space-y-3">
        {filtered.map((s) => {
          const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.new;
          const isExpanded = expanded === s.signup_id;
          const isApproved = s.status === 'approved' || s.status === 'onboarded';
          const endDate = s.beta_end_date || 'September 30, 2026';

          return (
            <div key={s.signup_id} className="bg-white border-2 border-gray-200 overflow-hidden" data-testid={`beta-signup-${s.signup_id}`}>
              {/* Header Row */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(isExpanded ? null : s.signup_id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <School size={18} className="text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-sm text-gray-800 truncate">{s.school_name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{s.name} {s.title ? `— ${s.title}` : ''} | {s.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {isApproved && (
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] text-gray-400 uppercase">Ends</p>
                      <p className="text-xs font-bold text-gray-700">{endDate}</p>
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    {s.grade_bands?.join(', ') || 'N/A'}
                  </div>
                  {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t-2 border-gray-100 p-4 bg-gray-50 space-y-4">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-gray-400 block">Name</span><span className="font-semibold">{s.name}</span></div>
                    <div><span className="text-gray-400 block">Title</span><span>{s.title || 'N/A'}</span></div>
                    <div><span className="text-gray-400 block">Email</span><a href={`mailto:${s.email}`} className="text-[#002FA7]">{s.email}</a></div>
                    <div><span className="text-gray-400 block">Phone</span><span>{s.phone || 'N/A'}</span></div>
                    <div><span className="text-gray-400 block">School</span><span className="font-semibold">{s.school_name}</span></div>
                    <div><span className="text-gray-400 block">District</span><span>{s.district || 'N/A'}</span></div>
                    <div><span className="text-gray-400 block">Grade Bands</span><span className="font-semibold text-[#002FA7]">{s.grade_bands?.join(', ') || 'N/A'}</span></div>
                    <div><span className="text-gray-400 block">ELL Students</span><span>{s.estimated_ell_students || 'N/A'}</span></div>
                    <div><span className="text-gray-400 block">How Heard</span><span>{s.how_heard || 'N/A'}</span></div>
                    <div><span className="text-gray-400 block">Submitted</span><span>{s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : 'N/A'}</span></div>
                    {isApproved && (
                      <>
                        <div><span className="text-gray-400 block">Approved</span><span>{s.approved_at ? new Date(s.approved_at).toLocaleDateString() : 'N/A'}</span></div>
                        <div><span className="text-gray-400 block">Beta Ends</span><span className="font-bold text-amber-700">{endDate}</span></div>
                      </>
                    )}
                    {s.admin_email && (
                      <div className="col-span-2"><span className="text-gray-400 block">Admin Credentials</span><code className="text-xs bg-white px-2 py-1 border">{s.admin_email}</code></div>
                    )}
                  </div>

                  {s.message && (
                    <div className="bg-white p-3 border-l-3 border-[#002FA7] text-xs text-gray-600">
                      <strong>Message:</strong> {s.message}
                    </div>
                  )}

                  {/* Extension History */}
                  {s.extensions?.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 p-3">
                      <p className="text-xs font-bold text-blue-800 mb-2">Extension History</p>
                      {s.extensions.map((ext, i) => (
                        <div key={i} className="text-xs text-blue-700 mb-1">
                          {ext.previous_end_date} → <strong>{ext.new_end_date}</strong>
                          {ext.reason && <span className="text-blue-500 ml-1">({ext.reason})</span>}
                          <span className="text-blue-400 ml-1">— {new Date(ext.extended_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Data Retention Notice */}
                  {(s.status === 'expired' || s.status === 'cancelled') && s.data_retention_until && (
                    <div className="bg-orange-50 border border-orange-200 p-3">
                      <p className="text-xs font-bold text-orange-800 mb-1">Data Retention</p>
                      <p className="text-xs text-orange-700">
                        Student data preserved until: <strong>{new Date(s.data_retention_until).toLocaleDateString()}</strong>
                        {s.data_purged && <span className="text-red-600 font-bold ml-2">[PURGED]</span>}
                      </p>
                    </div>
                  )}

                  {/* Reminders Sent */}
                  {s.reminders_sent?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 p-3">
                      <p className="text-xs font-bold text-amber-800 mb-1">Reminders Sent</p>
                      <p className="text-xs text-amber-700">{s.reminders_sent.sort((a, b) => b - a).map(d => `${d}-day`).join(', ')}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                    {/* Status Actions */}
                    {s.status === 'new' && (
                      <>
                        <Button size="sm" onClick={() => updateStatus(s.signup_id, 'contacted')} className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-contact-${s.signup_id}`}>
                          <Mail size={13} className="mr-1" /> Mark Contacted
                        </Button>
                        <Button size="sm" onClick={() => updateStatus(s.signup_id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-approve-${s.signup_id}`}>
                          <Check size={13} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" onClick={() => updateStatus(s.signup_id, 'declined')} variant="outline" className="text-red-500 border-red-200 text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-decline-${s.signup_id}`}>
                          <X size={13} className="mr-1" /> Decline
                        </Button>
                      </>
                    )}
                    {s.status === 'contacted' && (
                      <>
                        <Button size="sm" onClick={() => updateStatus(s.signup_id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-approve-${s.signup_id}`}>
                          <Check size={13} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" onClick={() => updateStatus(s.signup_id, 'declined')} variant="outline" className="text-red-500 border-red-200 text-xs h-8" style={{ borderRadius: 0 }}>
                          <X size={13} className="mr-1" /> Decline
                        </Button>
                      </>
                    )}
                    {isApproved && (
                      <>
                        {s.status === 'approved' && (
                          <Button size="sm" onClick={() => updateStatus(s.signup_id, 'onboarded')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" style={{ borderRadius: 0 }}>
                            <Users size={13} className="mr-1" /> Mark Onboarded
                          </Button>
                        )}
                        <Button size="sm" onClick={() => setExtending(extending === s.signup_id ? null : s.signup_id)} variant="outline" className="text-blue-600 border-blue-200 text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-extend-${s.signup_id}`}>
                          <CalendarPlus size={13} className="mr-1" /> Extend
                        </Button>
                        <Button size="sm" onClick={() => setConfirmCancel(confirmCancel === s.signup_id ? null : s.signup_id)} variant="outline" className="text-red-500 border-red-200 text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-cancel-${s.signup_id}`}>
                          <Ban size={13} className="mr-1" /> Cancel Access
                        </Button>
                        <Button size="sm" onClick={() => downloadAgreement(s.signup_id)} variant="outline" className="text-gray-600 border-gray-200 text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-agreement-${s.signup_id}`}>
                          <Download size={13} className="mr-1" /> Agreement PDF
                        </Button>
                        <Button size="sm" onClick={() => downloadSchoolData(s.school_name)} variant="outline" className="text-emerald-600 border-emerald-200 text-xs h-8" style={{ borderRadius: 0 }} data-testid={`beta-export-${s.signup_id}`}>
                          <Download size={13} className="mr-1" /> Export School Data
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Extend Form */}
                  {extending === s.signup_id && (
                    <div className="bg-blue-50 border-2 border-blue-200 p-4 space-y-3" data-testid={`beta-extend-form-${s.signup_id}`}>
                      <p className="text-xs font-bold text-blue-800">Extend Beta Access</p>
                      <p className="text-xs text-blue-600">Current end date: <strong>{endDate}</strong></p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 block mb-1">New End Date</label>
                          <select
                            value={extendDate}
                            onChange={e => setExtendDate(e.target.value)}
                            className="w-full h-9 border-2 border-blue-300 px-3 text-sm bg-white"
                            data-testid="extend-date-select"
                          >
                            <option value="">Select...</option>
                            <option value="December 31, 2026">December 31, 2026 (+3 months)</option>
                            <option value="March 31, 2027">March 31, 2027 (+6 months)</option>
                            <option value="June 30, 2027">June 30, 2027 (+9 months)</option>
                            <option value="September 30, 2027">September 30, 2027 (+1 year)</option>
                          </select>
                          <Input
                            value={extendDate}
                            onChange={e => setExtendDate(e.target.value)}
                            placeholder="Or type custom date..."
                            className="h-8 text-xs mt-1 border-blue-200"
                            data-testid="extend-date-custom"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-gray-600 block mb-1">Reason (optional)</label>
                          <Input
                            value={extendReason}
                            onChange={e => setExtendReason(e.target.value)}
                            placeholder="e.g., School requested more time"
                            className="h-9 text-xs border-blue-200"
                            data-testid="extend-reason"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleExtend(s.signup_id)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" style={{ borderRadius: 0 }}>
                          <CalendarPlus size={13} className="mr-1" /> Confirm Extension
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setExtending(null); setExtendDate(''); setExtendReason(''); }} className="text-xs h-8" style={{ borderRadius: 0 }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Cancel Confirmation */}
                  {confirmCancel === s.signup_id && (
                    <div className="bg-red-50 border-2 border-red-300 p-4 space-y-3" data-testid={`beta-cancel-form-${s.signup_id}`}>
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={16} className="text-red-600" />
                        <p className="text-xs font-bold text-red-800">Cancel Beta Access for {s.school_name}?</p>
                      </div>
                      <p className="text-xs text-red-600">This will immediately deactivate their access. Student data will be preserved for 90 days. The school will be notified by email.</p>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-600 block mb-1">Reason (optional)</label>
                        <Input
                          value={cancelReason}
                          onChange={e => setCancelReason(e.target.value)}
                          placeholder="e.g., Non-responsive, violation of terms"
                          className="h-9 text-xs border-red-200"
                          data-testid="cancel-reason"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleCancel(s.signup_id)} className="bg-red-600 hover:bg-red-700 text-white text-xs h-8" style={{ borderRadius: 0 }}>
                          <Ban size={13} className="mr-1" /> Yes, Cancel Access
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setConfirmCancel(null); setCancelReason(''); }} className="text-xs h-8" style={{ borderRadius: 0 }}>
                          No, Keep Active
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FlaskConical size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No beta applications {statusFilter !== 'all' ? `with status "${statusFilter}"` : 'yet'}.</p>
        </div>
      )}
    </div>
  );
};

export default BetaManagementTab;
